import { AppModal } from '@Components/AppModal';
import { Size } from '@Components/designConstants';
import '@Pages/GeoMMM/ScenarioPlanning/Modals/SetPlanLengthModal.scss';
import { MdAdd, MdCircle, MdOutlineCircle, MdRemove } from 'react-icons/md';
import { useEffect, useMemo, useState, ReactElement } from 'react';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useSetError } from '../../../../redux/modals';

export const PLAN_LENGTH_MAP = { Week: 1, Month: 4, Quarter: 13, Year: 52 };

export const convertPlanLengthToWeeks = (
  planLength: number,
  planKey: 'Week' | 'Month' | 'Quarter' | 'Year'
): number => {
  // Handle the special case for Months first
  if (planKey === 'Month') {
    const baseWeeks = planLength * PLAN_LENGTH_MAP[planKey];
    const extraWeeks = Math.floor(planLength / 3);
    return baseWeeks + extraWeeks; // 12 + 1 = 13
  }
  return planLength * PLAN_LENGTH_MAP[planKey];
};

export const convertWeeksToPlanLength = (
  weeks: number,
  planKey: 'Week' | 'Month' | 'Quarter' | 'Year'
): number => {
  if (planKey === 'Month') {
    const numQuarters = Math.floor(weeks / 13);
    const remainingWeeks = weeks % 13;
    const remainderMonths = Math.min(Math.floor(remainingWeeks / 4), 2);
    const totalMonths = numQuarters * 3 + remainderMonths;
    return totalMonths;
  }
  return Math.floor(weeks / PLAN_LENGTH_MAP[planKey]);
};

const planSettingsForWeeks = (
  weeks: number | undefined
): { planKey: 'Week' | 'Month' | 'Quarter' | 'Year'; planLength: string } => {
  if (!weeks) {
    return { planKey: 'Week', planLength: '' };
  }
  if (weeks === 52) {
    return { planKey: 'Year', planLength: '1' };
  }
  if (weeks % 13 === 0) {
    return { planKey: 'Quarter', planLength: String(weeks / 13) };
  }
  for (let i = 1; i <= 12; i++) {
    if (convertPlanLengthToWeeks(i, 'Month') === weeks) {
      return { planKey: 'Month', planLength: String(i) };
    }
  }
  return { planKey: 'Week', planLength: String(weeks) };
};

export interface SetPlanLengtModalProps {
  currentPlanLength?: number;
  maxWeeks?: number;
  onApply: (planLength: number) => void;
  onHide: () => void;
  headerText?: string;
  infoText?: string;
}

export const SetPlanLengthModal = ({
  currentPlanLength,
  headerText = 'Scenario Plan Length',
  infoText = 'Reference dates will automatically update to match your plan length.',
  maxWeeks = 52,
  onApply,
  onHide
}: SetPlanLengtModalProps): ReactElement => {
  const [planKey, setPlanKey] = useState<'Week' | 'Month' | 'Quarter' | 'Year'>(
    () => planSettingsForWeeks(currentPlanLength).planKey
  );
  const [planLength, setPlanLength] = useState<string>(
    () => planSettingsForWeeks(currentPlanLength).planLength
  );
  const setError = useSetError();

  useEffect(() => {
    if (planLength && convertPlanLengthToWeeks(parseInt(planLength, 10), planKey) > maxWeeks) {
      const maxPlanLengthByKey: Record<typeof planKey, string> = {
        Week: `${maxWeeks}`,
        Month: '12',
        Quarter: '4',
        Year: '1'
      };
      setPlanLength(maxPlanLengthByKey[planKey]);
    }
  }, [maxWeeks, planKey, planLength]);

  const body = useMemo(
    () => (
      <div className='set-plan-length'>
        <div className='options'>
          {(Object.keys(PLAN_LENGTH_MAP) as (keyof typeof PLAN_LENGTH_MAP)[]).map(key => (
            <div key={key} className='option'>
              <InteractiveDiv className='select-icon' onClick={() => setPlanKey(key)}>
                {planKey === key ? <MdCircle /> : <MdOutlineCircle />}
              </InteractiveDiv>
              <InteractiveDiv className='text' onClick={() => setPlanKey(key)}>
                {key}
                {key === 'Month' ? ' (4 weeks)' : ''}
              </InteractiveDiv>
              {planKey === key && (
                <div className='input-container'>
                  <InteractiveDiv
                    className={`decrease-icon ${['', '1'].includes(planLength) ? 'disabled' : ''}`}
                    onClick={() => {
                      if (!['', '1'].includes(planLength)) {
                        setPlanLength((parseInt(planLength, 10) - 1).toString());
                      }
                    }}
                  >
                    <MdRemove />
                  </InteractiveDiv>
                  <div className='input'>
                    <input
                      onChange={e => {
                        // Strip everything but digits so typed/pasted non-numbers (e.g. "abc"
                        // or a trailing "5abc") can never become the stored value.
                        const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
                        if (digitsOnly === '') {
                          setPlanLength('');
                          return;
                        }
                        const newPlanLength = parseInt(digitsOnly, 10);
                        const totalWeeks = convertPlanLengthToWeeks(newPlanLength, planKey);
                        if (newPlanLength > 0 && totalWeeks <= maxWeeks) {
                          setPlanLength(digitsOnly);
                        }
                      }}
                      inputMode='numeric'
                      type='text'
                      value={planLength}
                    />
                  </div>
                  <InteractiveDiv
                    className={`increase-icon ${
                      convertPlanLengthToWeeks(parseInt(planLength, 10) || 0, planKey) >= maxWeeks
                        ? 'disabled'
                        : ''
                    }`}
                    onClick={() => {
                      const current = parseInt(planLength, 10) || 0;
                      if (convertPlanLengthToWeeks(current + 1, planKey) <= maxWeeks) {
                        setPlanLength((current + 1).toString());
                      }
                    }}
                  >
                    <MdAdd />
                  </InteractiveDiv>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className='info'>{infoText}</div>
      </div>
    ),
    [infoText, maxWeeks, planKey, planLength]
  );
  const handleApply = (): void => {
    const numWeeks = convertPlanLengthToWeeks(parseInt(planLength, 10), planKey);
    if (!planLength || Number.isNaN(numWeeks)) {
      setError({ message: 'Please enter a plan length' });
    } else if (numWeeks > maxWeeks) {
      setError({ message: `Plan length cannot exceed ${maxWeeks} weeks` });
    } else if (numWeeks < 1) {
      setError({ message: 'Invalid plan length' });
    } else {
      onApply(numWeeks);
    }
  };

  return (
    <AppModal
      show
      onHide={onHide}
      title={headerText}
      size={Size.SM}
      primaryAction={{ label: 'Apply', onClick: handleApply }}
      secondaryAction={{ onClick: onHide }}
    >
      {body}
    </AppModal>
  );
};
